import { Component, EventEmitter, OnInit, Output, computed, effect, input, model, signal, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { AppAction } from '@pflegix/authorization';
import { getDirtyValues } from '@pflegix/form';
import { I18NextAdapterPipe, I18nService, RECURRENCE_NAMESPACE } from '@pflegix/i18n';
import {
  BusinessPartnerType,
  DetailedRecurrenceFrequency,
  OrderPopulatedResponseDto,
  SERVICE_SETTLEMENT_TYPE_CONFIG,
  ServiceSettlementType,
} from '@pflegix/types';
import {
  CheckboxComponent,
  DatePickerComponent,
  DropdownComponent,
  DropdownOption,
  FormBaseComponent,
  FormWrapperComponent,
  GenderDropdownComponent,
  LanguageMultiSelectComponent,
  MultiSelectComponent,
  MultiSelectOption,
  NumberInputComponent,
  TextInputComponent,
  TextareaInputComponent,
} from '@pflegix/ui';
import { AddressUtilService } from '@pflegix/util';
import { filter, startWith, switchMap, tap } from 'rxjs';
import { BusinessPartnerApi } from '../../../../business-partner-management/api/business-partner.api';
import { OrderFormApi } from '../../api/order-form.api';
import { ORDER_FORM_NAMESPACE } from '../../i18n/const/order-form-namespace.const';
import { ORDER_FORM_TRANSLATION_DE } from '../../i18n/de/order-form.translation.de';
import { ORDER_FORM_TRANSLATION_EN } from '../../i18n/en/order-form.translation.en';
import { OrderFormService } from '../../service/order-form.service';
import { OrderFormGroup, OrderFormGroupValue } from '../../type/order-form-group.type';

import { ATTENDANCES_TO_SETTLEMENT_TYPE_MAPPING } from '../../const/attendances-to-settlement-type-mapping.const';

@Component({
  selector: 'pfx-order-form',
  imports: [
    ReactiveFormsModule,
    FormWrapperComponent,
    DropdownComponent,
    GenderDropdownComponent,
    MultiSelectComponent,
    LanguageMultiSelectComponent,
    DatePickerComponent,
    CheckboxComponent,
    NumberInputComponent,
    TextareaInputComponent,
    TextInputComponent,
    I18NextAdapterPipe,
  ],
  providers: [OrderFormService, OrderFormApi],
  templateUrl: './order-form.component.html',
})
export class OrderFormComponent extends FormBaseComponent implements OnInit {
  protected readonly orderFormService = inject(OrderFormService);
  private readonly orderFormApi = inject(OrderFormApi);
  private readonly businessPartnerApi = inject(BusinessPartnerApi);
  private readonly i18n = inject(I18nService);

  public order = input<OrderPopulatedResponseDto>();

  @Output() public submitForm = new EventEmitter<OrderFormGroupValue>();

  public loading = model(false);

  protected appAction = computed<AppAction>(() => (this.order() ? 'update' : 'create'));

  protected i18nNamespace = ORDER_FORM_NAMESPACE;

  protected settlementTypeOptions: DropdownOption[] = [
    {
      label: SERVICE_SETTLEMENT_TYPE_CONFIG[ServiceSettlementType.PFLEGIX_INTERNAL].label,
      value: ServiceSettlementType.PFLEGIX_INTERNAL,
    },
    {
      label: SERVICE_SETTLEMENT_TYPE_CONFIG[ServiceSettlementType.SGB_V_S38_HOME_HELP].label,
      value: ServiceSettlementType.SGB_V_S38_HOME_HELP,
    },
    {
      label: SERVICE_SETTLEMENT_TYPE_CONFIG[ServiceSettlementType.SGB_XI_S45B_CARE].label,
      value: ServiceSettlementType.SGB_XI_S45B_CARE,
    },
    {
      label: SERVICE_SETTLEMENT_TYPE_CONFIG[ServiceSettlementType.SGB_XI_S45C_SCHOOLING].label,
      value: ServiceSettlementType.SGB_XI_S45C_SCHOOLING,
    },
    {
      label: SERVICE_SETTLEMENT_TYPE_CONFIG[ServiceSettlementType.SGB_XI_S37_PAR3_COUNSELLING_MISSION].label,
      value: ServiceSettlementType.SGB_XI_S37_PAR3_COUNSELLING_MISSION,
    },
  ];

  protected selectedSettlementType = toSignal(
    this.orderFormService.form.controls.settlementType.valueChanges.pipe(
      startWith(ServiceSettlementType.SGB_XI_S45B_CARE)
    )
  );

  protected attendancesOptions = computed<MultiSelectOption[]>(() => {
    const selectedSettlementType = this.selectedSettlementType();

    if (!selectedSettlementType) {
      return [];
    }

    const options = ATTENDANCES_TO_SETTLEMENT_TYPE_MAPPING[selectedSettlementType];

    return options || [];
  });

  protected frequencyOptions: DropdownOption[] = [];

  protected organizationOptions = signal<DropdownOption[]>([]);

  protected caregiverOptions = signal<DropdownOption[]>([]);

  public get form(): OrderFormGroup {
    return this.orderFormService.form;
  }

  constructor() {
    super();

    effect(() => {
      const order = this.order();

      if (!order) {
        return;
      }

      this.orderFormService.prefill(order);
    });

    effect(() => {
      if (this.appAction() === 'create') {
        this.form.controls.notes.disable();
      } else {
        this.form.controls.notes.enable();
      }
    });

    this.i18n.addTranslations({
      namespace: ORDER_FORM_NAMESPACE,
      de: ORDER_FORM_TRANSLATION_DE,
      en: ORDER_FORM_TRANSLATION_EN,
    });

    this.handleOrganizationChanges();
  }

  public ngOnInit(): void {
    this.setOrganizationOptions();
    this.setFrequencyOptions();
  }

  public loadingFinished(): void {
    this.loading.set(false);
  }

  protected handleSubmit(): void {
    const values = getDirtyValues(this.orderFormService.form);

    this.submitForm.emit(values);
  }

  protected handleCancel(): void {
    this.orderFormService.reset(this.order());
  }

  private handleOrganizationChanges(): void {
    this.orderFormService.form.controls.organization.valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter((selectedOrganization) => !!selectedOrganization),
        switchMap((selectedOrganization) =>
          this.orderFormApi.getCaregiversOfOrganization(selectedOrganization).pipe(
            tap((caregivers) => {
              this.caregiverOptions.set(
                caregivers
                  .map((caregiver) => ({
                    label: AddressUtilService.generateNameReverse(caregiver.mainAddress),
                    value: caregiver._id,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))
              );
            })
          )
        )
      )
      .subscribe();
  }

  private setOrganizationOptions(): void {
    this.businessPartnerApi
      .getBusinessPartners(`type$eq$${BusinessPartnerType.ORGANIZATION}`)
      .pipe(
        tap((businessPartners) => {
          const options =
            businessPartners
              ?.map((businessPartner) => ({
                label: businessPartner.mainAddress.name,
                value: businessPartner._id,
              }))
              .sort((a, b) => a.label.localeCompare(b.label)) || [];

          this.organizationOptions.set(options);
        })
      )
      .subscribe();
  }

  private setFrequencyOptions(): void {
    this.frequencyOptions = Object.keys(DetailedRecurrenceFrequency).map((key) => ({
      label: this.i18n.t(key.toLowerCase(), { ns: RECURRENCE_NAMESPACE }),
      value: key,
    }));
  }
}
